import { apiRequest, HttpMethod } from '@hqo/shared-modules/dist/api-request';
import { QueryKey, useQuery } from '@tanstack/react-query';
import { useApi } from 'app/shared/contexts/api';
import { useBuilding } from 'app/shared/contexts/building';
import { useIntl } from 'react-intl';

export interface SpotlightCategory {
  uuid: string;
  name: string;
}

// Keyed by locale as well as building: `lang` is part of what the request asks for, so without it a
// locale change serves the previous language's names from cache instead of refetching.
export const getSpotlightCategoriesQueryKey = (buildingUuid: string, locale: string): QueryKey => [
  'spotlightCategories',
  buildingUuid,
  locale,
];

export const useSpotlightCategoriesQuery = () => {
  const { building } = useBuilding();
  const intl = useIntl();
  const { apiUrl, getAuthHeaders, getDefaultHeaders } = useApi() as unknown as {
    apiUrl: string;
    getAuthHeaders: () => Promise<{ Authorization: string }>;
    getDefaultHeaders: (buildingUuid?: string) => Record<string, string>;
  };

  return useQuery({
    queryKey: getSpotlightCategoriesQueryKey(building?.uuid ?? '', intl.locale),
    queryFn: async ({ signal }): Promise<SpotlightCategory[]> => {
      const authHeaders = await getAuthHeaders();
      const defaultHeaders = getDefaultHeaders(building?.uuid);
      const headers = { ...authHeaders, ...defaultHeaders };

      // `lang` follows the USER, not the building (VIS-1578): it selects which words to render, and a
      // Slovak user in an en-US building should read Slovak category names next to the Slovak content
      // that use-content-query already returns. Sourcing it from building.locale is what puts English
      // category chips on an otherwise-translated screen.
      const params = new URLSearchParams({ categoryType: 'SecondaryContent', lang: intl.locale });
      const payload = await apiRequest<{ data: { categories: SpotlightCategory[] } }, void, void>({
        url: `${apiUrl}/content/tenant/v3/buildings/${building?.uuid}/categories?${params.toString()}`,
        method: HttpMethod.GET,
        headers,
        signal,
      });

      return payload.isEmpty() ? [] : payload.data.data.categories;
    },
  });
};
